import { BaseFirestoreData } from "../../util/base-firestore";
import { getEmptyOrganization, Organization } from "../organization";
import { type UploadFile } from "../../model/file";
import { DateTime } from "luxon";
import { type ContactData, ContactDataDefault } from "../../model/contact";
import { Contact, getEmptyContact } from "../contact";
import { Client, getEmptyClient } from "../client";

export const DocumentTypes = ["invoice", "quotation", "deliveryNote"] as const;
export type DocumentType = (typeof DocumentTypes)[number];

// "basic" の実装は必須
export const themes = {
  invoice: {
    basic: {
      name: "ベーシック",
    },
    basic2: {
      name: "ベーシック2",
    },
    basic3: {
      name: "ベーシック3",
    },
  },
  deliveryNote: {
    basic: {
      name: "ベーシック",
    },
    basic2: {
      name: "ベーシック2",
    },
    basic3: {
      name: "ベーシック3",
    },
  },
  quotation: {
    basic: {
      name: "ベーシック",
    },
    basic2: {
      name: "ベーシック2",
    },
    basic3: {
      name: "ベーシック3",
    },
  },
} as const;
export type ThemeID<P extends DocumentType> = keyof (typeof themes)[P];

// テーマ設定
export interface ThemeOption {
  // 項目の非表示
  showCompanyLogo: boolean;
  showCompanyStamp: boolean;
  showTitle: boolean;
  showBillingDate: boolean;
  showPaymentDue: boolean;

  showExpirationDate: boolean;
  showQuantityUnit: boolean;
  showUnitPrice: boolean;
  showQuantity: boolean;
  showDeliveryDate: boolean;
  showInvoiceNumber: boolean;

  showOrderTitle: boolean;

  // 宛先の調整
  showClientCompanyName: boolean;
  showClientDepartmentName: boolean;
  showClientDepartmentUserName: boolean;
  showClientAddress: boolean;
}

export const DefaultThemeOptions: ThemeOption = {
  showCompanyLogo: true,
  showCompanyStamp: true,
  showTitle: true,
  showBillingDate: true,
  showPaymentDue: true,
  showExpirationDate: true,
  showQuantityUnit: true,
  showQuantity: true,
  showUnitPrice: true,
  showDeliveryDate: true,
  showInvoiceNumber: true,

  showOrderTitle: true,
  showClientCompanyName: true,
  showClientDepartmentName: true,
  showClientDepartmentUserName: true,
  showClientAddress: true,
};

// 表示設定
export interface DisplaySetting<P extends DocumentType> {
  themeId: ThemeID<P>;
  themeOptions: ThemeOption;
}

export const getDefaultDisplaySetting = (paperType: DocumentType): DisplaySetting<typeof paperType> => {
  return {
    themeId: "basic",
    themeOptions: DefaultThemeOptions,
  };
};

// 内税、外税、
export const TaxCalcTypes = ["内税", "外税"] as const;
export type TaxCalcType = (typeof TaxCalcTypes)[number];

export const TaxTypes = {
  "10%": 0.1,
  "8%(軽減税率)": 0.08,
  // "8%": 0.08,
  // "5%": 0.05,
  "免税": 0,
  "非課税": 0,
  "不課税": 0,
} as const;
export type TaxType = keyof typeof TaxTypes;

export const TaxRoundTypes = ["切り捨て", "四捨五入", "切り上げ"] as const;
export type TaxRoundType = (typeof TaxRoundTypes)[number];

export const TransferFeeBearers = ["自社負担", "相手方負担", "(非表示)"] as const;
export type TransferFeeBearer = (typeof TransferFeeBearers)[number];

export interface TaxOption {
  type: TaxCalcType; //外税 内税
  showZeroTax: boolean; // 消費税が0円のときに、消費税項目を表示するかどうか
  roundType: TaxRoundType;
}

export const DefaultTaxOption: TaxOption = {
  type: "外税",
  showZeroTax: false,
  roundType: "四捨五入",
};

export interface CalcSetting {
  taxOption: TaxOption;
}

export const DefaultCalcSetting: CalcSetting = {
  taxOption: DefaultTaxOption,
};

export interface PaperItem {
  itemId: string;
  deliveryDate?: DateTime; // 発行日
  name: string; // 品目
  unitPrice: number; //単価
  quantity: number; //数量
  quantityUnit: string; // 式 とか 個 とか
  taxPercentType: TaxType; //税率
  detail: string; //詳細
  enable: boolean; // 有効/無効
  doNotMultiplyUnitPriceByQuantity?: boolean; // 単価に数量を掛けない
}

export const getEmptyPaperItem = (): PaperItem => {
  return {
    itemId: ulid(),
    detail: "",
    name: "",
    quantity: 1,
    quantityUnit: "個",
    taxPercentType: "10%",
    unitPrice: 0,
    deliveryDate: DateTime.now(),
    enable: true,
  };
};

export interface TaxTypeSum {
  type: TaxType;
  percent: number;
  sum: number;
  taxSum: number;
  priceSum: number;
}

export interface CalculatedPrices {
  items: (PaperItem & { price: number })[];
  withoutTaxSum: number;
  taxSum: number;
  taxSums: TaxTypeSum[];
  price: number;
}

import { groupBy } from "lodash";
import { Invoice } from "./invoice";
import { Quotation } from "./quotation";
import { DeliveryNote } from "./delivery-note";
import { ulid } from "ulid";
export const calcPrices = (paper: BasePaper): CalculatedPrices => {
  let items = [];
  let withoutTaxSum = 0;
  let taxSum = 0;
  let taxSums = [];

  items = paper.items
    .filter((i) => i.enable)
    .map((i) => {
      return {
        ...i,
        price: i.doNotMultiplyUnitPriceByQuantity ? i.unitPrice : i.unitPrice * i.quantity,
      };
    });

  const round = (val: number) => {
    switch (paper.calcSetting.taxOption.roundType) {
      case "切り捨て":
        return Math.floor(val);
      case "四捨五入":
        return Math.round(val);
      case "切り上げ":
        return Math.ceil(val);
    }
  };

  if (paper.calcSetting.taxOption.type === "外税") {
    let price = 0;
    const taxTypeItems = groupBy(items, "taxPercentType");
    taxTypeItems["10%"] ??= [];
    taxTypeItems["8%(軽減税率)"] ??= [];
    for (const _taxType in taxTypeItems) {
      const taxType = _taxType as TaxType;
      const priceSum = taxTypeItems[taxType].reduce((p, c) => p + c.price, 0);
      withoutTaxSum += round(priceSum);
      const tax = priceSum * TaxTypes[taxType];

      const roundTaxSum = round(tax);
      taxSums.push({
        type: taxType,
        percent: TaxTypes[taxType],
        sum: priceSum,
        taxSum: roundTaxSum,
        priceSum: priceSum + roundTaxSum,
      });
      taxSum += roundTaxSum;
    }
    taxSums = taxSums.sort((a, b) => b.percent - a.percent);

    price = withoutTaxSum + taxSum;

    return {
      items,
      withoutTaxSum,
      taxSum,
      taxSums,
      price,
    };
  } else if (paper.calcSetting.taxOption.type === "内税") {
    let price = 0;
    const taxTypeItems = groupBy(items, "taxPercentType");
    taxTypeItems["10%"] ??= [];
    taxTypeItems["8%(軽減税率)"] ??= [];
    for (const _taxType in taxTypeItems) {
      const taxType = _taxType as TaxType;
      const _priceSum = taxTypeItems[taxType].reduce((p, c) => p + c.price, 0);
      price += _priceSum;

      const priceSum = round(_priceSum / (1 + TaxTypes[taxType]));
      withoutTaxSum += priceSum;
      const tax = priceSum * TaxTypes[taxType];

      const roundTaxSum = round(tax);
      taxSums.push({
        type: taxType,
        percent: TaxTypes[taxType],
        sum: _priceSum - roundTaxSum,
        taxSum: roundTaxSum,
        priceSum: priceSum + roundTaxSum,
      });
      taxSum += roundTaxSum;
    }
    taxSums = taxSums.sort((a, b) => b.percent - a.percent);

    return {
      items,
      withoutTaxSum,
      taxSum,
      taxSums,
      price,
    };
  }
  throw new Error("undefined tax type");
};

export interface BasePaperDefaultSettings<P extends DocumentType> {
  displaySetting: DisplaySetting<P>;
  calcSetting: CalcSetting;
  transferFeeBearer: TransferFeeBearer;
}

export const getEmptyBasePaperDefaultSettings = (
  documentType: DocumentType
): BasePaperDefaultSettings<typeof documentType> => {
  return {
    displaySetting: getDefaultDisplaySetting(documentType),
    calcSetting: DefaultCalcSetting,
    transferFeeBearer: "相手方負担",
  };
};

export class BasePaper extends BaseFirestoreData {
  documentType: DocumentType;
  // 表示設定
  displaySetting: {
    [K in DocumentType]: DisplaySetting<K>;
  }[DocumentType];

  // 計算設定
  calcSetting: CalcSetting;

  // 振込手数料を負担する人
  transferFeeBearer: TransferFeeBearer;

  // 送信側情報
  // 組織
  // 自動で自社の組織IDが入る
  organizationId: string;
  organization: Organization;
  // 自社担当者
  // 自社担当者はすべて消せるので、一応nullを許容する
  // 設定していないと送信はできないが、未設定の状態でも保存はできる
  contactId: string | null;
  contact: Contact | null;
  // 振込先口座は請求書にしか表示しないので、ここには書かない

  // 受信側情報
  // 組織
  // 設定していないと送信はできないが、未設定の状態でも保存はできる
  clientId: string | null;
  client: Client | null;
  // 自社担当者
  // 設定していないと送信はできないが、未設定の状態でも保存はできる
  clientContactId: string | null;
  clientContact: ContactData | null;

  // 添付ファイル関係
  files?: UploadFile[]; // ユーザがアップしたファイル
  generatedFiles?: UploadFile[]; // システムが生成したファイル

  // 通し番号 文字も可
  no: string;

  // タイトル
  title: string;

  // 備考
  description: string;

  // 明細
  items: PaperItem[];

  // 計算済み各金額
  // 納品書にも書くこともあるらしいので。
  prices: CalculatedPrices;

  // 表示する作成日
  displayedCreatedAt: DateTime;

  // タグID
  tagIds: string[];

  // 権限関連
  createdUserId: string; // 作成した人
  updatedUserId: string; // 最後に更新した人
  assignee: string[]; // 担当者ID

  // 送信・既読管理関係
  downloadId: string | null; // ダウンロードID　送信時に生成される、受取側はこのIDを使ってダウンロードする
  submittedAt: DateTime | null; // 送信済なら日付が入る, ruleに使っている、保存するときにこれを変更するとACLでコケるので、おくらない
  markReadAt: DateTime | null; // 読まれた時刻
  resendedAt?: DateTime[] | null; //再送日時　複数回送ることがあるので配列

  // 送信予約・リマインド関係
  submitScheduledAt: DateTime | null; // 送信予定時刻 未送信の請求書はこの時間以降に送る
  scheduledSubmitError?: string;
  remindSubmitError?: string;
}

export const getEmptyBasePaper = (documentType: DocumentType): BasePaper => {
  return {
    documentType,
    ...getEmptyBasePaperDefaultSettings(documentType),
    organizationId: "",
    organization: getEmptyOrganization(),
    contactId: null,
    contact: null,

    clientId: "",
    client: getEmptyClient(),
    clientContactId: "",
    clientContact: ContactDataDefault,

    files: [],
    no: "",
    title: "",
    description: "",
    displayedCreatedAt: DateTime.now(),
    items: [],
    prices: {
      items: [],
      withoutTaxSum: 0,
      taxSum: 0,
      taxSums: [],
      price: 0,
    },
    tagIds: [],

    createdUserId: "",
    updatedUserId: "",
    assignee: [],

    downloadId: null,
    submittedAt: null,
    markReadAt: null,
    resendedAt: null,
    submitScheduledAt: null,

    createdAt: DateTime.now(),
    updatedAt: DateTime.now(),
    deletedAt: null,
  };
};

export type BasePapers = Invoice | Quotation | DeliveryNote;
